export interface ChangelogVersion {
  version: string;
  date: string;
  content: string;
}

export const changelogVersions: ChangelogVersion[] = [
  {
    version: '0.9.9',
    date: 'March 22, 2026',
    content: `# What's New in Naptune 0.9.9

This update introduces our most requested feature—Family Plans—along with important improvements to upload defaults, subscription management, and critical bug fixes for PayPal integration.

---

## New Features

**Family Plan Subscription Tier**
- Introducing the **Family Plan**, perfect for small teams, collaborations, or households!
- **Up to 4 accounts** included with a single subscription—share Pro-level features with your crew.
- Family Plan includes all Pro features: unlimited uploads, scheduling, bulk upload, crop image, upload defaults, and more.
- **Owner Dashboard**: Family plan owners can manage their members, invite new users, and remove members as needed.
- **Automatic Member Detection**: Members automatically receive Family tier access when added to an active plan.
- Family Plans are now available on the pricing page—check it out!

**Number of Accounts Display**
- Subscription comparison tables now clearly show how many accounts are included with each tier.
- **Free/Starter/Pro**: 1 account
- **Family Plan**: 4 accounts
- This makes it easier to understand what you're getting with each subscription level.

---

## Upload Experience Improvements

**Default Privacy Status Changed to Public**
- Upload defaults now use "public" as the default privacy status instead of "private".
- This aligns better with typical user workflows where most uploads are intended to be public.
- You can still change this to "private" or "unlisted" for individual uploads or in your upload defaults.

**Improved Category Dropdown**
- Fixed issues with the YouTube category dropdown to ensure smooth selection and proper form validation.

---

## Subscription & Payment Reliability

**PayPal Integration Fixes**
- Fixed critical PayPal SDK compatibility issues that could prevent subscription checkout from completing.
- Improved error handling to prevent unhandled promise rejections during PayPal payment flows.
- Relaxed security headers (Cross-Origin-Opener-Policy) to ensure PayPal popup windows work correctly across all browsers.
- Enhanced subscription tier loading and verification after purchase.

**Family Plan Subscription Flow**
- Streamlined the subscription upgrade flow for Family Plans.
- Fixed bugs where family member role assignments weren't being applied correctly.
- Improved family plan creation process to ensure smooth onboarding.

---

## Permission System Enhancements

**Family Plan Menu Visibility**
- Family Plan management menu now uses the permission system for proper access control.
- Only family plan owners see the Family Plan menu item—members don't see it to avoid confusion.

**Role Consolidation**
- Simplified family plan roles: consolidated to a single "family" role with dynamic owner/member detection.
- This improves performance and reduces database complexity.
- Hidden the base family role from UI to prevent confusion.

---

## User Interface Improvements

**Landing Page Pricing Section**
- Enhanced pricing section with improved carousel navigation for mobile users.
- Easier to browse and compare subscription tiers on smaller screens.

**Login & Authentication**
- Fixed login restoration logic to ensure session persistence works correctly.
- Improved Google Sign-In button reliability.

---

Thank you for your continued support! The Family Plan has been one of our most requested features, and we're excited to bring it to you. As always, keep the feedback coming—it drives everything we do!`
  },
  {
    version: '0.9.8',
    date: 'February 22, 2026',
    content: `# What's New in Naptune 0.9.8

This update brings quality-of-life improvements to your upload workflow, better subscription management for special cases, and important bug fixes based on your feedback.

---

## Upload Experience Improvements

**Streamlined Login Experience**
- Removed the old standalone login screen - now \`/upload\` serves as your entry point!
- When you're not logged in, the upload page displays the Google sign-in button directly in the interface.
- After signing in, you're immediately on the upload page, ready to go—no extra redirects or steps.
- Cleaner, faster flow that gets you uploading in fewer clicks.

**Upload Data Persistence Across OAuth Login**
- Your upload data (audio, images, and all form fields) now persists when you sign in via Google OAuth!
- Previously, if you added files and filled in metadata before signing in, everything would be lost after the OAuth redirect.
- Now your work is automatically saved and restored after login—no more re-uploading or re-entering data.
- Data automatically expires after 1 hour for security and freshness.
- Works seamlessly on both mobile and desktop.

**Watermark Control in Upload Defaults**
- You can now disable the watermark directly in your Upload Default presets (Starter & Pro tiers).
- Previously, you had to toggle watermark on/off for each individual upload—now your preference is saved in your preset.
- Perfect if you consistently upload without watermarks and want to save time.

**Track Container UX Polish**
- Fixed track numbering: When you delete a track in bulk upload mode, the remaining tracks now properly renumber (1, 2, 3...) in sequence.
- Added smooth animations when adding or removing tracks, making it clearer when changes happen.
- Improved visual feedback for a more polished upload experience.

---

## Subscription & Access

**Better Handling for Admin-Role Users**
- If you have admin-assigned special access but no active subscription, the subscription page now shows you a clearer, more appropriate view instead of confusing you with upgrade prompts.
- This mainly affects beta testers and team members with special access permissions.

---

## Bug Fixes

**Crop Image Now Available for Starter & Complimentary Users**
- Fixed an issue where Starter and Complimentary tier users couldn't access the Crop Image feature even though it was supposed to be available for those tiers.
- If you've been locked out of image cropping on Starter tier, you can now use it as intended!

**Better Error Messages for Non-Gmail Users**
- If you try to sign up with a non-Gmail account, you'll now see a clear, helpful error message explaining that Naptune currently requires a Gmail account for Google OAuth authentication.
- Previously this would fail silently or show a confusing generic error.

**Audio Limit Adjustments**
- Fine-tuned audio upload limits for test and complimentary accounts to better match tier capabilities.

---

## Landing Page Updates

**New Sections & Content**
- Added Community section highlighting our growing user base
- Added Save Time section showcasing efficiency benefits
- Added Stay In Control section emphasizing user control and flexibility
- Improved SEO and discoverability for new users finding Naptune

---

## Behind the Scenes

**Audio Analysis Infrastructure**
- Laid the groundwork for upcoming features related to audio similarity detection and smart recommendations.
- This is preparation for future features—stay tuned for what's coming next!

**Admin Tools Improvements**
- Enhanced admin panel for better support and troubleshooting (doesn't affect your day-to-day use, but helps us help you faster when issues arise).
- Full mobile responsiveness across all admin panels - manage your platform on the go!

---

Thank you for your continued feedback and support—keep the suggestions coming!`
  },
  {
    version: '0.9.7',
    date: 'February 12, 2026',
    content: `# What's New in Naptune 0.9.7

This is a **major update** packed with powerful new features, enhanced upload capabilities, and critical reliability improvements. We're excited to bring you personalized profiles, flexible watermark controls, unlimited free uploads, and much more!

---

## New Features

**Leaderboard Profiles (Pro Only)**
- Pro users can now create personalized profiles to stand out in the community.
- **Hide Your Email**: Choose to display a custom username instead of your email on the leaderboard.
- **Bio**: Share your story, achievements, or production style with other producers.
- **Featured Video**: Showcase your best work uploaded via Naptune—think of it as your CV for the community.
- All profile fields are optional, giving you full control over your public presence.

**Watermark Control**
- Watermarks are now integrated into the upload flow.
- **Free Users**: Watermark is mandatory to ensure free uploads are attributed to Naptune.
- **Starter & Pro Users**: Watermark is optional—upload with or without branding as you prefer.
- Watermarks display "Uploaded via naptune.xyz" on your videos.

**Unlimited Instant Uploads for Free Users**
- Free tier now supports **unlimited instant uploads** (non-scheduled).
- Upload as many tracks as you want, when you want—no daily limits!
- Only scheduled uploads remain tier-restricted.

**Audio Upload Limits by Tier**
We've introduced tier-based audio file restrictions to optimize server performance and ensure a smooth experience for all users:
- **Free Tier**: Up to **10MB** audio files, **MP3 format only**
- **Starter Tier**: Up to **25MB** audio files, formats: **MP3, M4A, AAC, OGG**
- **Pro Tier**: Up to **75MB** audio files, formats: **MP3, M4A, AAC, OGG, WAV, FLAC, AIFF**

**Limited-Time Promotions System**
- Backend logic for limited-time promotions is now live.
- Soon you'll be able to enjoy exclusive discounts and special offers on subscription plans.
- Stay tuned for our first promotional campaign!

**Improved Error Messaging**
- More precise error messages when issues occur during YouTube authentication or upload.
- **YouTube Channel Not Found**: Clear notification if your YouTube channel doesn't exist or isn't accessible.
- **Insufficient Permissions**: Specific error if you don't grant the necessary YouTube scopes during signup—no more guessing what went wrong.

---

## User Interface Improvements

**Animated Track Containers**
- Added smooth animations when adding or removing tracks in bulk upload mode.
- Track numbers now dynamically renumber (1, 2, 3...) when tracks are deleted, maintaining a clean sequential order.
- Visual feedback makes it easier to track changes when managing multiple uploads.

---

## Subscription Activation Reliability

**Mobile Subscription Activation Fixed**
- Fixed critical issue where mobile users who completed PayPal checkout would sometimes not receive their subscription tier despite successful payment.
- Root cause: Mobile browsers can suspend or kill tabs during PayPal redirect flow, preventing the frontend verification callback from completing.

**Multiple Safety Nets for Subscription Activation**
- Enhanced backend subscription activation with multiple redundant mechanisms to ensure subscriptions are activated even if the browser closes during checkout.
- Improved subscription tracking with custom identifiers that link directly to your account, ensuring subscriptions are never orphaned.

---

## What This Means For You

**For All Users**:
- Enjoy clearer error messages that help you resolve issues faster.
- Free users now have unlimited instant uploads—create and share without limits!

**For Pro Users**:
- Build your presence with customizable leaderboard profiles.
- Upload lossless audio formats (WAV, FLAC, AIFF) up to 75MB—perfect for high-fidelity production.

**For Previous Subscription Issues**:
- If you previously experienced issues where your payment went through but you didn't receive access to your subscription tier, this update ensures that will no longer happen. The system now has multiple redundant mechanisms to catch and activate your subscription, even if your browser closes or loses connection during the checkout process.

---

Thank you for your continued support and feedback—this update wouldn't be possible without you!`
  },
  {
    version: '0.9.6',
    date: 'February 5, 2026',
    content: `# What's New in Naptune 0.9.6

This is our first release since the soft launch, and we've been blown away by the feedback you've shared—thank you! Based on what we've heard, this update focuses on polishing the upload experience, giving you better visibility when things go wrong, and making powerful features more accessible across tiers.

---

## Error Tracking Improvements

**Better Feedback When Something Goes Wrong**
- Youtube upload errors encountered during uploads are now surfaced directly to you with clear, actionable messages instead of failing silently.
- Improved error reporting also helps us on our end to identify and fix issues faster, so we can keep improving the experience for you.

---

## Tune Upload UX Improvements

**Auto Scroll on New Track**
- When adding a new track in bulk upload mode, the view now automatically scrolls to the newly added track so it's immediately visible and ready to configure.

**Numbered Upload Containers**
- Each upload container is now clearly numbered, making it easier to keep track of your tracks when working with multiple uploads at once.

---

## Upload Defaults

**Clone Upload Defaults**
- You can now clone an existing upload default preset with a single click. This makes it much faster to create similar presets without starting from scratch each time.

**Instant Sync Across Pages**
- Creating, editing, or deleting an upload default now immediately reflects on the Tune Upload page without requiring a page refresh.

---

## Landing Page Improvements

**Demo Video UX Overhaul**
- Improved the presentation and interaction of the demo video on the landing page for a smoother, more engaging first impression.

---

## Feature Adjustments

**Crop Image → Starter Tier**
- Crop Image has been moved down to the Starter tier, giving more users access to this useful feature earlier.

**Auto Resizer → Free Tier**
- Auto Resizer is now available on the Free tier. We want every user to have a smooth upload experience from day one, so we've made this core tool freely accessible.

---

Thank you for your continued feedback—it's what drives these improvements!`
  },
  {
    version: '0.9.5',
    date: 'February 3, 2026',
    content: `# What's New in Naptune 0.9.5

Welcome to our **Soft Launch / Pre-Alpha Release**! This is an exciting milestone for Naptune, and we're thrilled to have you here.

---

## What Does "Soft Launch" Mean?

**Soft Launch**: We're officially opening Naptune to the public with our live payment system! This means you can now sign up, choose a subscription plan that fits your needs, and start uploading to YouTube with ease.

**Role-Based Access**: Features are now gated by subscription tiers. Choose the plan that works best for you—from our free tier for basic uploads to Pro for advanced features like bulk uploads, scheduling, and leaderboard access.

**Beta Tester Recognition**: A huge thank you to our beta testers! All beta participants will receive complimentary Pro access as a token of our appreciation for helping us refine Naptune.

**Pre-Alpha Status**: While the core functionality is solid, we're actively iterating based on user feedback. You might encounter occasional bugs or rough edges as we polish the experience toward our 1.0 release.

**Your Feedback Matters**: We're committed to continuously improving Naptune based on your input. Report issues, suggest features, and help us build the best YouTube upload tool possible.

---

## User Experience Improvements

**Cleaner Interface**
- Removed unnecessary icons from navigation, subscriptions, and landing page for a more focused and modern look.

**Leaderboard Refinement**
- Users with 0 uploads are now excluded from leaderboard rankings for a cleaner, more meaningful display.

---

## Bug Fixes & Security

**Enhanced Webhook Security**
- Fixed an issue where PayPal webhook authentication failures (invalid signatures) were being incorrectly logged as 500 Internal Server Errors instead of 401 Unauthorized errors.
- Added comprehensive security logging for failed webhook authentication attempts.
- Webhook authentication failures now return a friendly (but firm!) error message to unauthorized callers.
- Improved error tracking accuracy for webhook-related security events.

---

## What's Next?

We're committed to continuing to find ways to improve the app and user experience. Stay tuned for regular updates, new features, and refinements as we work toward our 1.0 release!

Thank you for being part of our journey. Your support and feedback mean everything to us!`
  },
  {
    version: '0.9.2',
    date: 'January 23, 2026',
    content: `# What's New in Naptune 0.9.2

We've been hard at work improving the billing experience and enhancing transparency around subscription events. This update brings several important fixes and improvements to how payments and subscriptions are handled.

---

## Billing & Subscription Improvements

**Refund Handling**
- Fixed refund processing: When upgrading to a new subscription tier, if you cancel the PayPal subscription approval, the prorate amount is now properly refunded.

**Enhanced Subscription History**
- Subscription history now tracks more comprehensive event data, including lifetime access purchases and detailed payment information.
- Better visibility into all subscription-related events for easier troubleshooting and transparency.

**Lifetime Access Integration**
- After purchasing lifetime access, your subscription limit now refreshes automatically—no more manual refreshes required!

**Subscription Upgrade Fix**
- Fixed a critical bug: When upgrading from Starter Monthly to Pro Annual, if you cancelled the PayPal subscription agreement after paying the prorate amount, you would incorrectly be subscribed to Starter Annual. This now correctly returns you to your original Starter Monthly subscription.

**Proration Calculation Fix**
- Fixed incorrect proration after free billing cycle changes: When switching from Starter Monthly to Starter Annual (free), then upgrading to Pro Annual, the system now correctly calculates proration based on what you actually paid, not the annual plan price.

---

## User Experience Improvements

**Lifetime Purchase on Landing Page**
- Lifetime access is now prominently displayed on the landing page for easy discovery.

**Improved Lifetime Tab Display**
- Enhanced the lifetime access tab with clearer sale indicators.
- More accurate display of claimed spots to show real-time availability.

**Webhook Optimization**
- Removed unnecessary webhooks—we've moved to real-time synchronous PayPal API communication for better reliability and faster response times.

---

Thank you for your continued support and feedback!`
  },
  {
    version: '0.9.1',
    date: 'January 7, 2026',
    content: `# What's New in Naptune 0.9.1

Happy Holidays everyone! We really appreciate the feedback you've provided and have worked hard to improve your experience. We hope you're enjoying Naptune as much as we're enjoying making it. Your feedback is invaluable—please keep the suggestions coming!

---

## Feature Control & Access

**Leaderboard (Pro Feature)**
- Leaderboards are now a Pro-exclusive feature, enabling our most engaged users to collaborate and compete with each other.

**Scheduled Upload Limits**
- Scheduled uploads are now limited to 30 days in advance. This prevents abuse where users could schedule years of uploads with a single month's subscription, then cancel immediately.

---

## User Experience Improvements

**Improved Login Flow**
- When navigating to app.naptune.xyz from the landing page (naptune.xyz), you'll now be logged out by default and shown the login screen. Exception: clicking through subscription links will keep you logged in and redirect to the subscription page.

**Smoother Google OAuth**
- Fixed the annoying behavior where the app would immediately redirect you to Google login after 1 hour. Now the app attempts to refresh your token first, and only prompts for re-authentication (with a 3-second notification) if the refresh token has expired.

**Vertical Image Detection**
- Added a warning prompt when you upload a vertically-oriented image (height > width). Since YouTube automatically treats these as Shorts, you'll now get a heads-up before uploading, giving you a chance to crop your image properly.

**Square Image Crop Reminder**
- Images that are already square (equal width and height) now show a notification in the Crop Image modal, reminding you to apply the crop if you want to add a black background and export at 1920x1080 resolution.

**Upload Defaults Page Fixed**
- The Upload Defaults page no longer breaks and works reliably now.

**Auto Resizer Tooltip Updated**
- Updated Auto Resizer tooltip with correct demo image.

**Landing Page Improvements**
- Fixed Instagram and Discord links to be permanent and correct
- Fixed spelling mistake ("uploadss" → "uploads")
- Removed "Producers Like You" section temporarily
- Fixed Roadmap section overlapping on mobile devices

**Performance Monitoring**
- Administrators can now track upload performance metrics over time to identify potential degradation and opportunities for improvement.

---

Thank you for being our first users. Your feedback drives Naptune forward!`
  }
];

// Export the latest version for compatibility
export default changelogVersions[0].content;
